import {
  Button,
  ButtonProps,
  Flex,
  Heading,
  Image,
  Skeleton,
  Spacer,
  Text,
} from "@chakra-ui/react";
import DOMPurify from "dompurify";
import React from "react";
import { useSearchParams } from "react-router";
import { NotFound } from "src/components/Feedback/NotFound";
import { Card } from "src/components/Layout/Card";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { RouterLink } from "src/components/Links/RouterLink";
import { useBranding } from "src/components/Providers/BrandingProvider";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { useOrgConfig } from "src/hooks/useOrgConfig";
import { usePreferredLanguageBrowserOnly } from "src/hooks/usePreferredLanguage";
import { useValidateOrgPath } from "src/hooks/useValidateOrgPath";
import { WeglotSelect } from "src/plugins/weglot";
import { WEGLOT_APPLY_CLASS } from "src/plugins/weglot/constants";
import { getColorLogo } from "src/schemas/Branding";
import { isNonEmptyString, isNullOrUndefined } from "src/services/predicates";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";
import { DeepReadonly } from "src/types/utils";

export const Landing: React.FC = () => {
  const orgPath = useOrganizationPath();
  const accountLookupConfigRD =
    useOrgConfig<OrgConfig.AccountLookupConfig>("AccountLookup");
  const landingPageConfigRD =
    useOrgConfig<OrgConfig.LandingPageConfig>("LandingPage");

  const isValidOrgPathRD = useValidateOrgPath();

  if (!isNonEmptyString(orgPath)) {
    return null;
  }

  return (
    <RemoteDataView
      remoteData={RD.toTuple(isValidOrgPathRD, landingPageConfigRD)}
      error={() => null}
    >
      {([isValidOrgPath, landingPage]) => {
        if (!isValidOrgPath) {
          return <NotFound heading="404" text="Organization not found" />;
        }

        if (landingPage?.disabled !== false) {
          return <NotFound />;
        }

        return (
          <Flex minHeight="100vh">
            <LandingPage
              landingPage={landingPage}
              accountLookup={accountLookupConfigRD}
            />
          </Flex>
        );
      }}
    </RemoteDataView>
  );
};

type LandingPageProps = {
  landingPage: DeepReadonly<OrgConfig.LandingPageConfig>;
  accountLookup: RD.RemoteData<
    unknown,
    DeepReadonly<OrgConfig.AccountLookupConfig> | null
  >;
};
export const LandingPage: React.FC<LandingPageProps> = ({
  landingPage,
  accountLookup,
}) => {
  const branding = useBranding();
  const preferredLanguageSetting = usePreferredLanguageBrowserOnly();
  const logo = getColorLogo(branding);
  return (
    <Flex
      bg="white"
      sx={{ containerType: "inline-size" }}
      alignItems="stretch"
      direction="column"
      className={WEGLOT_APPLY_CLASS}
      flexGrow="1"
    >
      <Flex
        as="header"
        justifyContent="space-between"
        alignItems="center"
        padding={4}
      >
        <Image src={logo.url} alt={logo.alt} height="3em" maxWidth="9rem" />
        <Flex alignSelf="flex-start">
          <WeglotSelect
            colorScheme="black"
            variant="link"
            preferredLanguageSetting={preferredLanguageSetting}
          />
        </Flex>
      </Flex>
      <Flex as="main" grow="1" direction="column">
        {landingPage.banner?.enabled === true ? (
          <Banner content={landingPage.banner} />
        ) : null}
        <Flex
          padding="6"
          direction="column"
          sx={{
            "@container (width > 48em)": {
              flexDirection: "row",
            },
          }}
          gap="6"
          justifyContent="center"
        >
          {landingPage.welcomeCards?.map((card) => {
            return (
              <Card
                key={card.title}
                showShadow
                accentColor={
                  card.kind === "family" ? "primary.500" : "gray.700"
                }
                accentPosition="top"
                flex="1 1 0"
                maxW="100%"
                sx={{
                  "@container (width > 48em)": {
                    maxWidth: "24rem",
                  },
                }}
              >
                <Flex direction="column" gap="4">
                  <Heading
                    as="h2"
                    fontSize="1rem"
                    fontWeight="700"
                    color="gray.700"
                  >
                    {card.title}
                  </Heading>
                  <Text
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(card.description),
                    }}
                  />
                  {card.contents.map((content, index) => {
                    return (
                      <CardContent
                        key={content.kind}
                        content={content}
                        accountLookup={accountLookup}
                        index={index}
                      />
                    );
                  })}
                </Flex>
              </Card>
            );
          })}
        </Flex>
        <Flex
          bg="gray.50"
          grow="1"
          borderTop="1px solid"
          borderTopColor="gray.200"
          direction="column"
          padding="5"
          paddingY="7"
        >
          {landingPage.additionalContents?.map((content) => (
            <AdditionalContent
              key={`${content.title}|${content.description}`}
              content={content}
            />
          ))}
          <Spacer />
        </Flex>
      </Flex>
    </Flex>
  );
};

type WelcomeCardType = NonNullable<
  OrgConfig.LandingPageConfig["welcomeCards"]
>[number];
type CardContentType = WelcomeCardType["contents"][number];

const CardContent = (props: {
  content: CardContentType;
  accountLookup: RD.RemoteData<unknown, OrgConfig.AccountLookupConfig | null>;
  index: number;
}) => {
  const [searchParams] = useSearchParams();
  const orgPath = useOrganizationPath();
  const { content, accountLookup, index } = props;

  if (content.kind === "account-lookup-button") {
    if (accountLookup.isLoading() || accountLookup.isNotAsked()) {
      return <Skeleton width="100%" height="38px" />;
    }

    if (accountLookup.hasError()) {
      console.error(accountLookup.error);
      return null;
    }

    if (isNullOrUndefined(accountLookup.data) || accountLookup.data.disabled) {
      return null;
    }
  }

  if (isNullOrUndefined(orgPath)) {
    return null;
  }

  if (content.kind === "custom-content") {
    return (
      <Text
        fontSize="xs"
        textAlign="center"
        color="gray.700"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content.content),
        }}
      />
    );
  }

  let url = "";
  let variant: ButtonProps["variant"] = undefined;
  let colorScheme: ButtonProps["colorScheme"] = undefined;

  switch (index) {
    case 0: {
      variant = "solid";
      break;
    }
    case 1: {
      variant = "outline";
      break;
    }
    case 2: {
      variant = "ghost";
      break;
    }
  }

  switch (content.kind) {
    case "family-login-button": {
      url = Url.Login.parent(orgPath, "login", "email", searchParams);
      break;
    }
    case "family-signup-button": {
      url = Url.Login.parent(orgPath, "signup", "email", searchParams);
      break;
    }
    case "account-lookup-button": {
      url = Url.Parent.AccountLookup.index({ path: orgPath });
      break;
    }
    case "staff-login-button": {
      url = Url.Login.admin(orgPath);
      colorScheme = "gray";
      variant = "outline";
      break;
    }
  }

  return (
    <Button
      as={RouterLink}
      to={url}
      variant={variant}
      colorScheme={colorScheme}
    >
      {content.content}
    </Button>
  );
};

type BannerType = NonNullable<OrgConfig.LandingPageConfig["banner"]>;
const Banner = (props: { content: BannerType }) => {
  const { content } = props;
  return (
    <Flex
      color="white"
      sx={{
        "@container (width > 48rem)": {
          paddingY: "20",
          paddingX: "10",
        },
      }}
      paddingY="10"
      paddingX="5"
      direction="column"
      justifyContent="center"
      gap="4"
      background={`
        linear-gradient(
          to right,
          color-mix(in srgb, var(--chakra-colors-primary-500) 85%, transparent),
          color-mix(in srgb, var(--chakra-colors-primary-500) 45%, transparent),
          color-mix(in srgb, var(--chakra-colors-primary-500) 15%, transparent)
        ),
        url("${content.imageUrl}")
      `}
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      backgroundSize="cover"
    >
      <Heading as="h1" fontSize="2xl" fontWeight="700">
        {content.title}
      </Heading>
      <Text
        fontSize="md"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content.description ?? ""),
        }}
      />
    </Flex>
  );
};

type Content = NonNullable<
  OrgConfig.LandingPageConfig["additionalContents"]
>[number];
const AdditionalContent = (props: { content: Content }) => {
  const { content } = props;
  return (
    <Flex direction="column" flexGrow="1" basis="1" gap="4">
      <Heading as="h3" fontSize="1rem" fontWeight="700" textAlign="center">
        {content.title}
      </Heading>
      <Text
        fontSize="sm"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content.description),
        }}
      />
    </Flex>
  );
};
